import { useSuspenseQuery } from "@tanstack/react-query";
import { customerStateWidgetQueryOptions } from "@/features-root-user/service/dashboard";
import { z } from "zod";

// Re-define the lead opportunity shape if needed
export const leadOpportunitySchema = z.object({
  id: z.number(),
  owner: z.object({
    id: z.number(),
    picture: z.string().nullable().optional(),
    name: z.string(),
    email: z.string(),
    disabled: z.boolean().optional(),
  }),
  account: z.object({
    id: z.number(),
    picture: z.string().nullable().optional(),
    name: z.string(),
    hotness: z.number().optional(),
  }),
  stage: z.object({
    id: z.number(),
    name: z.string(),
    color: z.string(),
    fixed_stage: z.any().nullable(),
    order: z.number(),
    probability: z.number(),
  }),
  assignee: z.object({
    id: z.number(),
    picture: z.string().nullable().optional(),
    name: z.string(),
    email: z.string(),
  }),
  name: z.string().nullable(),
  custom: z
    .object({
      short_description: z.string().optional(),
      onboarding_price: z.number().optional(),
      node_hardware: z.string().optional(),
      node_qty: z.number().optional(),
      annual_subscription: z.number().optional(),
      node_data_service: z.string().optional(),
      subscription_type: z
        .object({
          id: z.number(),
          name: z.string(),
        })
        .optional(),
    })
    .nullable(),
  close_date: z.string(),
  creation_date: z.string().optional(),
  // Additional fields if needed
});

export type LeadOpportunity = z.infer<typeof leadOpportunitySchema>;

export function useLeadsAnalyticsData() {
  // Grab leads from the same query used by the "customers" page
  const { data: leadsRaw = [] } = useSuspenseQuery(
    customerStateWidgetQueryOptions,
  );
  // Validate/parse leads data
  const leads = z.array(leadOpportunitySchema).parse(leadsRaw);

  // Helper to calculate potential revenue: annual_subscription + onboarding_price
  const getPotentialRevenue = (lead: LeadOpportunity) => {
    const annual = lead.custom?.annual_subscription ?? 0;
    const onboarding = lead.custom?.onboarding_price ?? 0;
    return annual + onboarding;
  };

  // Weighted revenue by stage: sum of (probability * potentialRevenue) per stage
  const weightedRevenueByStageMap: Record<string, number> = {};
  leads.forEach((lead) => {
    const stageName = lead.stage.name;
    const prob = lead.stage.probability ?? 0;
    const potentialRevenue = getPotentialRevenue(lead);
    const weighted = potentialRevenue * prob;
    weightedRevenueByStageMap[stageName] =
      (weightedRevenueByStageMap[stageName] || 0) + weighted;
  });
  const weightedRevenueByStage = Object.entries(weightedRevenueByStageMap).map(
    ([stageName, weightedRevenue]) => ({
      stageName,
      weightedRevenue,
    }),
  );

  // Weighted revenue forecast by close_date month
  // We'll parse close_date's month-year to group the data
  interface WeightedRevenueByMonth {
    month: string;
    weightedRevenue: number;
  }

  const weightedByMonthMap: Record<string, number> = {};
  leads.forEach((lead) => {
    const closeDt = new Date(lead.close_date);
    if (isNaN(closeDt.getTime())) return;
    // Month format e.g. "2025-02" or "Feb 2025"
    const monthKey = closeDt.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    const prob = lead.stage.probability ?? 0;
    const potentialRevenue = getPotentialRevenue(lead);
    const weighted = potentialRevenue * prob;
    weightedByMonthMap[monthKey] =
      (weightedByMonthMap[monthKey] || 0) + weighted;
  });

  const weightedRevenueByMonth: WeightedRevenueByMonth[] = Object.entries(
    weightedByMonthMap,
  )
    .map(([month, weightedRevenue]) => ({ month, weightedRevenue }))
    // You may sort by chronological order:
    .sort((a, b) => {
      // parse "Feb 2025" etc. for sorting
      const dA = new Date(a.month);
      const dB = new Date(b.month);
      return dA.getTime() - dB.getTime();
    });

  // Recurring vs. One-time breakdown: sum all annual subs vs. sum all onboarding
  let totalRecurring = 0;
  let totalOnboarding = 0;
  leads.forEach((lead) => {
    const annual = lead.custom?.annual_subscription ?? 0;
    const onboarding = lead.custom?.onboarding_price ?? 0;
    totalRecurring += annual;
    totalOnboarding += onboarding;
  });

  const recurringOneTimeBreakdown = [
    { name: "Recurring (Annual)", value: totalRecurring },
    { name: "One-time (Onboarding)", value: totalOnboarding },
  ];

  return {
    leads,
    weightedRevenueByStage,
    weightedRevenueByMonth,
    recurringOneTimeBreakdown,
  };
}
